import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReplaySubject } from 'rxjs';
import { UserPermissions } from '../../../shared/interface/user-permissions';
import { AuthService } from '../../../shared/services/auth.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { HttpErrorResponse } from "@angular/common/http";
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from "@angular/forms";
import {delay, distinctUntilChanged, takeUntil} from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { getCurrentRouteState } from "../../../shared/ngrx/selector";
import { RouterStateUrl } from "../../../shared/ngrx/route-serializer";
import { FilterService } from "../../../shared/services/filter.service";
import { StoreRootState } from "../../../shared/ngrx/reducers";
import { Partner } from "../../../shared/interface/partner";
import { CountedAndPaginated } from '../../../shared/interface/response/counted-and-paginated';
import { Uuid } from "../../../shared/interface/response/uuid";
import { JobResultDialogComponent } from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import { MatSort, Sort } from '@angular/material/sort';
import {Profile} from "../../../shared/interface/profile";
import {BlockedNipAddDialogComponent} from "../blocked-nip-add-dialog/blocked-nip-add-dialog.component";
import {BlockedNipService} from "../../../shared/services/blocked-nip.service";
import {BlockedNip} from "../../../shared/interface/blockedNip";

@Component({
  selector: 'app-all-blocked-nip-list',
  templateUrl: './all-blocked-nip-list.component.html',
  styleUrls: ['./all-blocked-nip-list.component.scss']
})
export class AllBlockedNipListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @Input() embeddedView = false;
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();

  initialFilters: any;
  partnerObject: Partner;
  displayedColumns: string[] = [
    'nip',
    'bussinessName',
    'createdAt',
    'user',
    'regional',
    'actions',
  ];
  dataSource: BlockedNip[] = [];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = null;
  sortOrder = null;
  profile: Profile;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialog: MatDialog,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private snack: MatSnackBar,
    private auth: AuthService,
    private store: Store<StoreRootState>,
    private router: Router,
    private blockedNipService: BlockedNipService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.blockedNipService.getBlockedNipListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: CountedAndPaginated<BlockedNip>) => {
        this.dataSource = result.items;
        this.totalData = result.pagination.total;
      });
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      nip: 'string',
      businessName: 'string',
      contains$street: 'string',
      houseNumber: 'string',
      apartmentNumber: 'string',
      contains$postCity: 'string',
      zipCode: 'string',
      contains$city: 'string',
      contains$district: 'string',
      contains$commune: 'string',
      in$state: 'int[]',
    }, filters);
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'createdAt';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'DESC';
        this.loadListData();
      })
    ;
  }

  loadListData(): void {
    this.blockedNipService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
    );
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      ...this.filtersForm.value,
    })]);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  sortChanged(event: Sort): void {
    this.sortBy = event.active;
    this.sortOrder = event.direction.toUpperCase();
    this.loadListData();
  }

  addBlockedNip(): void {
    const dialogRef = this.dialog.open(BlockedNipAddDialogComponent, {
      width: '90%',
      height: '90%',
      maxHeight: '90%',
      panelClass: 'full-height-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
      this.changed.emit(true);
      this.dialog.closeAll();
    });
  }

  downloadReport(): void {
    this.blockedNipService.downloadReport(this.filtersForm.value)
      .subscribe((response: Uuid) => {
          this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: { jobId: response.uuid, showProgress: false },
          });
        },
        (error: HttpErrorResponse) => {
          this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), { duration: 2000 });
        }
      );
  }
  delete(blockedNip: BlockedNip): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć numer NIP z zablokowanych?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.blockedNipService.delete(blockedNip.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto numer NIP z zablokowanych', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }
}
